/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** The pieces the invite screen is drawn from. */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Text, TextInput, UnstyledButton } from '@/shared/ui';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import type { RowDraft, RowError } from './InviteTeam.rows';

/** The role select's width: the reference narrows it where the row wraps. */
export const ROLE_WIDTH = { desktop: 140, mobile: 112 };

/** The person glyph on the notice that the owner already holds a seat. */
export const PersonIcon: React.FC = () => (
  <svg
    width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" style={{ flex: 'none' }}
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" aria-hidden="true"
  >
    <path d="M12 5v14" /><path d="M5 12h14" />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" aria-hidden="true"
  >
    <path d="M18 6 6 18" /><path d="m6 6 12 12" />
  </svg>
);

interface InviteRowProps {
  row: RowDraft;
  index: number;
  planId: string;
  error: RowError;
  roleWidth?: number;
  /** The roles this line's block offers, in the order the select shows them. */
  roles: readonly string[];
  onEmail: (value: string) => void;
  onRole: (value: string) => void;
  onRemove: () => void;
  onPaste: (text: string) => void;
  onEnter: () => void;
}

/**
 * One typed line: an address, the role it arrives with, and a way out.
 *
 * The error sits under the row rather than replacing it, so the address the
 * operator typed stays on screen to be corrected.
 */
export const InviteRow: React.FC<InviteRowProps> = ({
  row, index, planId, error, roleWidth, roles,
  onEmail, onRole, onRemove, onPaste, onEnter,
}) => {
  const { t } = useTranslation('@pages/onboarding');
  // The role picker and the remove button take about 150px of a 390px phone,
  // which left the address field 138px for a placeholder needing 166 --
  // "nome@imobiliaria.com.br" was cut mid-domain, and the example address is
  // the whole point of showing one.
  const stacked = useMediaQuery('(max-width: 640px)');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <div
        style={{
          display: 'flex', gap: 'var(--space-2)',
          flexDirection: stacked ? 'column' : 'row',
          alignItems: stacked ? 'stretch' : 'center',
        }}
      >
        <TextInput
          id={row.id}
          data-testid={`invite-row-${planId}-${index}`}
          type="email"
          value={row.email}
          onChange={(event) => onEmail(event.target.value)}
          onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => {
            const text = event.clipboardData?.getData('text') || '';
            if (text.split(/[\s,;]+/).filter(Boolean).length > 1) {
              event.preventDefault();
              onPaste(text);
            }
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onEnter();
            }
          }}
          placeholder={t('inviteTeam.emailPlaceholder')}
          inputMode="email"
          aria-label={t('inviteTeam.emailLabel')}
          aria-invalid={error ? true : undefined}
          // The wrapper is what `style` reaches on this input, so the width
          // goes here and nothing about the field itself is restyled.
          style={{ flex: 1, minWidth: 0 }}
        />
        <div style={{ flex: 'none', width: roleWidth }}>
          <Select
            name={`${row.id}-role`}
            value={row.role}
            onChange={(value) => onRole(String(value))}
            aria-label={t('inviteTeam.roleLabel')}
            data={roles.map((role) => ({
              value: role, label: t(`inviteTeam.roles.${role}`),
            }))}
          />
        </div>
        <UnstyledButton
          onClick={onRemove}
          aria-label={t('inviteTeam.removeRow')}
          style={{
            flex: 'none', width: 32, height: 32, borderRadius: 'var(--radius-lg)',
            background: 'none', cursor: 'pointer', color: 'var(--text-tertiary-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <CloseIcon />
        </UnstyledButton>
      </div>
      {error && (
        <Text
          component="span"
          style={{
            fontSize: 'var(--text-tag)', lineHeight: 1.4,
            color: 'var(--status-error-text)',
          }}
        >
          {t(`inviteTeam.errors.${error}`)}
        </Text>
      )}
    </div>
  );
};

/**
 * The "Adicionar outro e-mail" line, offered while the plan has a seat left.
 *
 * @param props - The click handler and the plan it belongs to.
 * @returns The button.
 */
export const AddRowButton: React.FC<{ planId: string; onClick: () => void }> = ({
  planId, onClick,
}) => {
  const { t } = useTranslation('@pages/onboarding');
  return (
    <UnstyledButton
      onClick={onClick}
      data-testid={`invite-add-row-${planId}`}
      style={{
        alignSelf: 'flex-start', display: 'flex', alignItems: 'center',
        gap: 'var(--space-1-5)', height: 32, background: 'none', cursor: 'pointer',
        fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
        color: 'var(--seed-base)',
      }}
    >
      <PlusIcon />
      {t('inviteTeam.addRow')}
    </UnstyledButton>
  );
};

/**
 * The block's heading: what it is, how much of it is left, and — for the
 * auxiliary block — that it costs nothing.
 *
 * The badge earns its place by answering the question the block raises. A
 * row of free places sitting under three paid ones reads as something the
 * agency forgot to buy unless it says otherwise.
 */
export const BlockHeader: React.FC<{
  title: string; free: string; full: boolean; badge?: string | null;
}> = ({
  title, free, full, badge = null,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
    <Text
      component="span"
      style={{
        fontSize: 'var(--text-sm)', fontWeight: 'var(--font-bold)',
        color: 'var(--text-primary)',
      }}
    >
      {title}
    </Text>
    {badge ? (
      <Text
        component="span"
        data-testid="invite-block-badge"
        style={{
          fontSize: 'var(--text-tag)', borderRadius: 'var(--radius-full)',
          padding: '2px var(--space-2)',
          background: 'var(--brand-beige)', color: 'var(--seed-base)',
        }}
      >
        {badge}
      </Text>
    ) : null}
    <Text
      component="span"
      style={{
        fontSize: 'var(--text-meta)', fontWeight: 'var(--font-semibold)',
        color: full ? 'var(--text-tertiary-soft)' : 'var(--status-success-text)',
      }}
    >
      {free}
    </Text>
  </div>
);
